/**
 * @file Reviews.js
 * @author Stanley Chen
 * @description Component that displays customer reviews in a carousel format
 * along with online ordering options and promotional content
 */

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import './Reviews.css';

const reviewsData = [
    {
        id: 1,
        name: "Donna B.",
        text: "This was our first time at this restaurant. The atmosphere, ambiance, staff, chef, and food were great! Everyone was hospitable and we will definitely be going back.",
    },
    {
        id: 2,
        name: "Tomanise C.",
        text: "Delaware has a hidden gem. So so glad I went. Food was great and service was even greater.",
    },
    {
        id: 3,
        name: "Jeanne S.",
        text: "Totally impressed! Food had awesome flavor, ample amount, lots of variety with price being reasonable. Service was exceptional! Will visit again. Thank you!!!",
    },
    {
        id: 4,
        name: "Cailin Jay T.",
        text: "Absolutely delicious sushi! Service was fantastic and friendly, despite busy Sunday lunch crowds. Our server was spectacular. Warm, friendly, and caring- I'd go back simply to have her wait on me again.",
    },
    {
        id: 5,
        name: "Benny V.",
        text: "Everything was great!! From the waitress and our chef. They was very attentive and on point with everything. The food was amazing and the presentation for the food was great. We enjoyed ourselves.",
    },
    {
        id: 6,
        name: "Sam L",
        text: "It's a great family atmosphere/environment. Staff are great. Would definitely recommend, the manager/owner is there most/all the time and is really polite!!! Have fun every time I go!!",
    }

];

const Reviews = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const sectionRef = useRef(null);
    const orderRef = useRef(null);
    const reviewsRef = useRef(null);
    
    const isOrderInView = useInView(orderRef, { once: true, amount: 0.3 });
    const isReviewsInView = useInView(reviewsRef, { once: false, amount: 0.2 });

    const nextReview = () => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % reviewsData.length);
    };

    const prevReview = () => {
        setActiveIndex((prevIndex) => (prevIndex - 1 + reviewsData.length) % reviewsData.length);
    };

    const goToReview = (index) => {
        setActiveIndex(index);
    };

    const activeReview = reviewsData[activeIndex];

    // Container animation variants
    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    return (
        <section className="reviews-order-section" id="reviews-order" ref={sectionRef}>
            <img src="/images/mtnbg.jpg" alt="Background" className="section-background"/>
            <div className="section-overlay"></div>

            {/* Order Section */}
            <div className="order-container" ref={orderRef}>
                <div className="order-content">
                    <motion.div 
                        className="order-text"
                        initial={{ opacity: 0, x: 100 }}
                        animate={isOrderInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 100 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2>ORDER ONLINE</h2>
                        <p>
                            Enjoy our delicious meals from the comfort of your home. We offer convenient online ordering options to satisfy your cravings for authentic Japanese cuisine. Whether you're in the mood for sushi, hibachi, or any of our specialty dishes, we got you covered!
                        </p>
                        <div className="order-buttons">
                            <a href="https://kikude.kwickmenu.com/" rel="noopener noreferrer">
                                <motion.button
                                    className="primary-btn"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    ORDER FROM US
                                </motion.button>
                            </a>
                            <a
                                href="https://www.doordash.com/store/kiku-japanese-steakhouse-&-sushi-bar-middletown-579754/1499137/"
                                rel="noopener noreferrer"
                            >
                                <motion.button
                                    className="secondary-btn"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    DOORDASH
                                </motion.button>
                            </a>
                        </div>
                    </motion.div>
                    
                    <div className="order-image-wrapper">
                        <div className="order-border"></div>
                        <motion.div 
                            className="order-image"
                            initial={{ opacity: 0, x: -100 }}
                            animate={isOrderInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <img src="/images/gallery/gallery-20.jpg" alt="Order or Doordash" />
                        </motion.div>
                    </div>
                </div>
                <div className='divider'></div>
            </div>

            {/* Reviews Section */}
            <div className="reviews-container" ref={reviewsRef}>
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={isReviewsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="reviews-title"
                >
                    FROM OUR CUSTOMERS
                </motion.h2>
                
                <motion.div
                    className="reviews-subtitle-wrapper"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isReviewsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    <p className="reviews-subtitle">OVER 1000 5-STAR REVIEWS</p>
                    <div className="reviews-stars">
                        ★★★★★
                    </div>
                </motion.div>

                <motion.div 
                    className="reviews-carousel"
                    variants={container}
                    initial="hidden"
                    animate={isReviewsInView ? "show" : "hidden"}
                >
                    <button 
                        className="carousel-arrow left" 
                        onClick={prevReview}
                        aria-label="Previous review"
                    >
                        &#10094;
                    </button>
                    
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeReview.id}
                            className="review-card"
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ 
                                type: "spring", 
                                stiffness: 300, 
                                damping: 30, 
                                duration: 0.4 
                            }}
                        >
                            <div className="review-content">
                                <div className="review-text-container">
                                    <span className="quote-left">&#8220;</span>
                                    <p className="review-text">{activeReview.text}</p>
                                    <span className="quote-right">&#8221;</span>
                                </div>
                                <div className="review-author">
                                    <h4 className="review-name">-{activeReview.name}</h4>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                    
                    <button 
                        className="carousel-arrow right" 
                        onClick={nextReview}
                        aria-label="Next review"
                    >
                        &#10095;
                    </button>
                </motion.div>

                <motion.div 
                    className="review-dots"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isReviewsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    {reviewsData.map((_, index) => (
                        <button
                            key={index}
                            className={`dot ${index === activeIndex ? 'active' : ''}`}
                            onClick={() => goToReview(index)}
                            aria-label={`Go to review ${index + 1}`}
                        />
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default Reviews;